import { withStyles } from '@material-ui/styles';
import { flowRight, isUndefined } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from '@material-ui/core';

import { isDefined } from '../../../utils/isDefined';
import Optional from '../../../utils/optional';
import styles from '../styles/rangesDetails';
import usePagination from '../../../utils/usePagination';
import CustomFormControlLabel from '../../../partials/customFormControlLabel';
import { translate } from '../../../i18n/I18nProvider';
import ConnectNumberDetails from './ConnectNumberDetails';
import createNumbersArray from '../../../utils/createNumbersArray';
import { postData } from '../../../utils/http';
import { URLS } from '../../../consts/endpoints';
import mapOchData from '../actions/mapOchData';
import withUserCheck from '../../../utils/withUserCheck';

const PAGINATION_SIZE = 10;

function ConnectRangesDetails({ data, classes, mainSelected, isAdminUser }) {
  const [dataWithOch, setDataWithOch] = useState(data);

  const [foundedNumbers, setFoundedNumbers] = useState('true');

  useEffect(() => {
    (async function() {
      if (!isAdminUser) {
        return data;
      }
      const numbersArray = createNumbersArray(
        data.range_from,
        data.range_up_to_incl
      );
      try {
        const { data: ochResponse } = await postData({
          url: URLS.LOOKUP,
          data: {
            numbers: numbersArray
          }
        });

        if (isUndefined(ochResponse)) {
          return data;
        }

        const mappedData = mapOchData({ data, ochResponse });

        return setDataWithOch(mappedData);
      } catch {
        return data;
      }
    })();
  }, [mainSelected, data, isAdminUser]);

  const dataToShow = dataWithOch?.filteredNumber
    ? {
        ...dataWithOch,
        all: filterNumbers(dataWithOch.all, foundedNumbers),
        assigned: filterNumbers(dataWithOch.assigned_numbers, foundedNumbers),
        unassigned: filterNumbers(
          dataWithOch.unassigned_numbers,
          foundedNumbers
        )
      }
    : dataWithOch;

  const [selected, setSelected] = useState('all');

  useEffect(() => {
    setPage(0);
    setSelected(mainSelected);
  }, [mainSelected]);

  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_SIZE);
  const [page, setPage] = useState(0);
  const paginated = usePagination(
    Optional(dataToShow)
      .map(data => data[selected])
      .or([]),
    page,
    rowsPerPage
  );

  const onRadioButtonChange = e => {
    setPage(0);
    setSelected(e.target.value);
  };

  const onRadioButtonAllRangeChange = e => {
    setFoundedNumbers(e.target.value);
  };

  const onChangePage = useCallback((e, page) => setPage(page), [setPage]);
  const onChangeRowsPerPage = useCallback(e => setRowsPerPage(e.target.value), [
    setRowsPerPage
  ]);

  return isDefined(dataToShow) ? (
    <div className={classes.root}>
      {dataToShow.all.length > 1 && mainSelected === 'all' && (
        <div className={classes.radioContainer}>
          <RadioGroup
            row
            className={classes.radioButtons}
            value={selected}
            onChange={onRadioButtonChange}
          >
            <CustomFormControlLabel
              value="all"
              label={translate('BUTTON.ALL')}
            />
            <CustomFormControlLabel
              value="assigned"
              label={translate('ASSIGNED')}
            />
            <CustomFormControlLabel
              value="unassigned"
              label={translate('UNASSIGNED')}
            />
          </RadioGroup>
        </div>
      )}
      {isDefined(data?.mitelData?.filteredNumber) && (
        <div className={classes.radioContainer}>
          <RadioGroup
            row
            className={classes.radioButtons}
            value={foundedNumbers}
            onChange={onRadioButtonAllRangeChange}
          >
            <CustomFormControlLabel value="true" label={translate('DEFAULT')} />
            <CustomFormControlLabel
              value="false"
              label={translate('NUMBER_RANGES.FULL_RANGE')}
            />
          </RadioGroup>
        </div>
      )}
      <Table className={classes.tableRoot}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.iconCell}></TableCell>
            <TableCell className={classes.numberCell}>
              {translate('TABLE.NUMBER')}
            </TableCell>
            <TableCell className={classes.functionCell}>
              {translate('FUNCTION')}
            </TableCell>
            <TableCell className={classes.nameCell}>
              {translate('STATUS')}
            </TableCell>
            <TableCell className={classes.nameCell}>
              {translate('TABLE.ACTIVE_AT')}
            </TableCell>
            <TableCell className={classes.nameCell}>
              {translate('TABLE.INACTVE_AT')}
            </TableCell>
            {isAdminUser && (
              <TableCell className={classes.nameCell}>
                {translate('OPERATOR')}
              </TableCell>
            )}
            <TableCell className={classes.nameCell}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginated.map((data, i) => (
            <ConnectNumberDetails data={data} key={i} />
          ))}
        </TableBody>
      </Table>
      {dataToShow.all.length > PAGINATION_SIZE ? (
        <TablePagination
          className={classes.pagination}
          component="div"
          count={dataToShow[selected].length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          labelRowsPerPage={translate('TABLE.ROWS')}
        />
      ) : null}
    </div>
  ) : (
    <div className={classes.noData}>{translate('NUMBERS.NO_DATA_TO_SHOW')}</div>
  );
}

const mapStatesToProps = ({ externalNoRanges }) => {
  return {
    mainSelected: externalNoRanges.mainSelected
  };
};

export default flowRight(
  withUserCheck,
  connect(mapStatesToProps),
  withStyles(styles)
)(ConnectRangesDetails);

function filterNumbers(numbers, foundedNumbers) {
  return numbers.filter(number =>
    foundedNumbers === 'true' ? number.founded : true
  );
}
